import { AvailableLocale } from '@models/Lang'
import { QRCode, Typography } from 'antd'
import { CSSProperties } from 'react'
import { WALLET_LABELS } from '../labels'
import {
  FieldHighlightProps,
  ResolvedWalletCard,
  WalletCardAsset,
} from '../types'
import { useWalletAssetUrl } from '../useWalletAssetUrl'
import WalletFieldZone from '../WalletFieldZone'

const { Text } = Typography

const CARD_WIDTH = 340

interface Props extends FieldHighlightProps {
  card: ResolvedWalletCard
  /** The program's (localized) name, shown in the white pill badge. */
  programName?: string
  logo: WalletCardAsset
  /** The back-office language selector's current locale, for label translation. */
  locale?: AvailableLocale
}

/**
 * Mirrors the in-app member card exactly as table-booking-application's
 * MemberCardCallout renders it: 32px padding, 24px radius, 30px gap between the
 * three blocks; inside the top block the logo and (name + program badge) group
 * are spaced 24px, the name and badge 8px. Program logo on top when configured,
 * 32/36 name, white pill program badge (hidden when there's no program name,
 * just like the app), white QR frame, uppercase id label. Only the hardcoded
 * black/white colors are swapped for the configured ones; the badge and QR chip
 * stay white like the real app — they aren't part of the 3-color config.
 */
const AppMemberCardFace = ({
  card,
  programName,
  logo,
  locale = 'en',
  activeField,
  onActiveFieldChange,
}: Props) => {
  const logoUrl = useWalletAssetUrl(logo)
  const highlight = { activeField, onActiveFieldChange }
  const labels = WALLET_LABELS[locale]

  const logoStyle: CSSProperties = {
    width: 100,
    height: 32,
    objectFit: 'contain',
    alignSelf: 'flex-start',
    display: 'block',
  }

  return (
    <WalletFieldZone
      field="backgroundColor"
      radius={24}
      {...highlight}
      style={{
        width: CARD_WIDTH,
        backgroundColor: card.backgroundColor,
        padding: 32,
        display: 'flex',
        flexDirection: 'column',
        gap: 30,
        boxShadow: '0 12px 28px rgba(0, 0, 0, 0.22)',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {logoUrl ? (
          <WalletFieldZone
            field="logo"
            radius={6}
            {...highlight}
            style={{ alignSelf: 'flex-start' }}
          >
            <img src={logoUrl} alt="Logo" style={logoStyle} />
          </WalletFieldZone>
        ) : activeField === 'logo' ? (
          // No logo uploaded: reveal where it lands only while its uploader is
          // active, keeping the resting card faithful (the app shows no logo).
          <WalletFieldZone
            field="logo"
            radius={6}
            {...highlight}
            style={{ alignSelf: 'flex-start' }}
          >
            <div
              style={{
                ...logoStyle,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 6,
                background: `${card.foregroundColor}26`,
                color: card.foregroundColor,
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.6,
              }}
            >
              Logo
            </div>
          </WalletFieldZone>
        ) : (
          <></>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <WalletFieldZone
            field="foregroundColor"
            {...highlight}
            style={{ alignSelf: 'flex-start' }}
          >
            <Text
              style={{
                color: card.foregroundColor,
                fontSize: 32,
                lineHeight: '36px',
                fontWeight: 700,
              }}
            >
              {card.member.name}
            </Text>
          </WalletFieldZone>
          {programName ? (
            <div
              style={{
                alignSelf: 'flex-start',
                background: '#fff',
                borderRadius: 100,
                padding: '4px 12px',
              }}
            >
              <Text
                style={{
                  color: '#0f172a',
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                }}
              >
                {programName}
              </Text>
            </div>
          ) : (
            <></>
          )}
        </div>
      </div>

      <div
        style={{
          alignSelf: 'center',
          background: '#fff',
          borderRadius: 16,
          padding: 10,
        }}
      >
        <QRCode
          value={`plethore:member:${card.member.cardNumber}`}
          size={192}
          bordered={false}
          color="#000"
          bgColor="#fff"
        />
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <WalletFieldZone field="labelColor" {...highlight}>
          <Text
            style={{
              color: card.labelColor,
              fontSize: 12,
              fontWeight: 700,
              textTransform: 'uppercase',
            }}
          >
            {labels.cardNumber}
          </Text>
        </WalletFieldZone>
        <Text
          style={{ color: card.foregroundColor, fontSize: 16, fontWeight: 700 }}
        >
          {card.member.cardNumber}
        </Text>
      </div>
    </WalletFieldZone>
  )
}

export default AppMemberCardFace
